import { appStore, parseFullSetlistZip } from '../core/store';
import { APP_VERSION } from '../core/version';
import type { BackupFileV1, RoutingSide, SetlistCountInOverride, SetlistRouting } from '../core/types';
import { buildRehearsalRouting, parseRehearsalRouting, DEFAULT_REHEARSAL_ROUTING, MARKER_LEAD_IN_OPTIONS } from '../core/types';
import { confirmDialog, promptDialog } from '../ui/confirm';
import { showToast } from '../ui/toast';

export function renderSetlistsSection(container: HTMLElement): () => void {
  container.innerHTML = '';
  const setlistRouteTarget = window.location.hash.replace(/^#\//, '').split('/')[1];

  const wrapper = document.createElement('div');
  wrapper.innerHTML = `
    <div class="route-page-header setlists-page-header">
      <div>
        <h1>Setlists</h1>
        <p class="text-dim">Build a show, choose its playback mode, and put it in the Player.</p>
      </div>
      <div class="setlists-page-actions">
        <button class="btn" id="setlist-tools-btn" aria-expanded="false" aria-controls="setlist-tools-panel">Import / Export</button>
        <button class="btn btn-primary" id="new-setlist-btn">+ New Setlist</button>
      </div>
    </div>
    <div class="setlist-tools-panel" id="setlist-tools-panel" hidden>
      <div class="setlist-transfer-option">
        <div>
          <strong>Share a playable setlist</strong>
          <p class="text-dim">Includes songs, audio, markers, click settings, and setlist order.</p>
        </div>
        <div class="row">
          <button class="btn btn-ready" id="export-full-setlist-btn">Export .zip</button>
          <button class="btn" id="import-full-setlist-btn">Load .zip</button>
          <input type="file" id="full-setlist-file-input" accept="application/zip,.zip" style="display:none" />
        </div>
      </div>
      <div class="setlist-transfer-option is-secondary">
        <div>
          <strong>Settings-only backup</strong>
          <p class="text-dim">JSON backup without audio files. Audio must be imported again on a new device.</p>
        </div>
        <div class="row">
          <button class="btn" id="export-backup-btn">Export JSON</button>
          <button class="btn" id="import-backup-btn">Import JSON</button>
          <input type="file" id="backup-file-input" accept="application/json,.json" style="display:none" />
        </div>
      </div>
    </div>
    <div id="setlist-container" class="setlist-accordion"></div>
  `;
  container.appendChild(wrapper);

  const setlistContainer = wrapper.querySelector<HTMLDivElement>('#setlist-container')!;
  const newSetlistBtn = wrapper.querySelector<HTMLButtonElement>('#new-setlist-btn')!;
  const exportBtn = wrapper.querySelector<HTMLButtonElement>('#export-backup-btn')!;
  const importBtn = wrapper.querySelector<HTMLButtonElement>('#import-backup-btn')!;
  const backupInput = wrapper.querySelector<HTMLInputElement>('#backup-file-input')!;
  const exportFullSetlistBtn = wrapper.querySelector<HTMLButtonElement>('#export-full-setlist-btn')!;
  const importFullSetlistBtn = wrapper.querySelector<HTMLButtonElement>('#import-full-setlist-btn')!;
  const fullSetlistInput = wrapper.querySelector<HTMLInputElement>('#full-setlist-file-input')!;
  const toolsBtn = wrapper.querySelector<HTMLButtonElement>('#setlist-tools-btn')!;
  const toolsPanel = wrapper.querySelector<HTMLDivElement>('#setlist-tools-panel')!;

  let dragFromIndex: number | null = null;
  let dragSetlistId: string | null = null;
  let expandedSetlistId: string | null = setlistRouteTarget ? decodeURIComponent(setlistRouteTarget) : null;
  let openActionsSetlistId: string | null = null;

  function render(): void {
    const { setlists, songs, settings } = appStore.getState();
    setlistContainer.innerHTML = '';

    if (setlists.length === 0) {
      setlistContainer.innerHTML = '<p class="empty-state">No setlists yet. Create one to start building your show.</p>';
      return;
    }

    for (const setlist of setlists) {
      const isExpanded = expandedSetlistId === setlist.id;
      const isActive = settings.activeSetlistId === setlist.id;
      const card = document.createElement('div');
      card.className = `setlist-editor-card ${isExpanded ? 'is-expanded' : 'is-collapsed'}${setlist.id === setlistRouteTarget ? ' is-route-target' : ''}`;
      card.dataset.setlistId = setlist.id;

      const header = document.createElement('div');
      header.className = 'setlist-summary';
      header.innerHTML = `
        <button class="setlist-summary-toggle" data-action="toggle-setlist" data-id="${setlist.id}" aria-expanded="${isExpanded}" aria-controls="setlist-body-${setlist.id}">
          <span class="setlist-chevron" aria-hidden="true">›</span>
          <span class="setlist-summary-copy">
            <span class="setlist-summary-name">${escapeHtml(setlist.name)}</span>
            <span class="setlist-summary-meta">${setlist.entries.length} ${setlist.entries.length === 1 ? 'song' : 'songs'} · ${setlist.mode === 'live' ? 'Live' : 'Rehearsal'}</span>
          </span>
        </button>
        <div class="setlist-summary-actions">
          ${isActive ? '<span class="setlist-active-badge">Active</span>' : `<button class="btn btn-small" data-action="use" data-id="${setlist.id}">Use in Player</button>`}
          <button class="btn btn-small setlist-more-btn" data-action="toggle-setlist-actions" data-id="${setlist.id}" aria-label="More actions for ${escapeHtml(setlist.name)}" aria-expanded="${openActionsSetlistId === setlist.id}">•••</button>
          ${openActionsSetlistId === setlist.id ? `
            <div class="setlist-actions-menu">
              <button data-action="rename" data-id="${setlist.id}">Rename</button>
              <button data-action="duplicate" data-id="${setlist.id}">Duplicate</button>
              <button data-action="export-setlist" data-id="${setlist.id}">Export .zip</button>
              <button class="is-danger" data-action="delete" data-id="${setlist.id}">Delete</button>
            </div>` : ''}
        </div>
      `;
      card.appendChild(header);

      const body = document.createElement('div');
      body.className = 'setlist-editor-body';
      body.id = `setlist-body-${setlist.id}`;
      body.hidden = !isExpanded;

      const modeRow = document.createElement('div');
      modeRow.className = 'setlist-mode-row';
      modeRow.innerHTML = `
        <div class="setlist-mode-control" role="group" aria-label="Playback mode">
          <button class="${setlist.mode === 'rehearsal' ? 'is-selected' : ''}" data-action="set-mode" data-mode="rehearsal" data-id="${setlist.id}">Rehearsal</button>
          <button class="${setlist.mode === 'live' ? 'is-selected' : ''}" data-action="set-mode" data-mode="live" data-id="${setlist.id}">Live</button>
        </div>
        <span class="text-dim">${setlist.mode === 'rehearsal' ? 'Scrub and jump between markers while practicing.' : 'Navigation is locked to prevent accidental jumps on stage.'}</span>
      `;
      body.appendChild(modeRow);

      // Every song in this setlist plays through this SAME routing — a
      // setlist-level setting (not per-song, see SetlistRouting's own doc
      // comment) so a show's PA/in-ear split can't drift song to song. A
      // song with no active click always plays stereo regardless of this
      // choice (there's nothing to protect from PA bleed); a metronome-only
      // song still routes its own click to whichever side is chosen here.
      //
      // Rehearsal and Live get genuinely different controls, not just
      // different options in one shared list: Rehearsal has no PA to
      // protect, so it gets two fully independent Track/Click selects (9
      // combinations — narrowing the track itself is a legitimate choice
      // here, unlike before when it was locked to always-stereo). Live
      // keeps its original single <select> with only the 2 genuine PA/
      // in-ear splits — deliberately NOT opened up the same way, since
      // that constraint (the click must never bleed into the PA side) is
      // a real safety property of a live show, not an arbitrary limit.
      const routingRow = document.createElement('div');
      routingRow.className = 'setlist-mode-row';
      if (setlist.mode === 'rehearsal') {
        const rehearsal = parseRehearsalRouting(setlist.routing) ?? parseRehearsalRouting(DEFAULT_REHEARSAL_ROUTING)!;
        const sideOptions = (selected: RoutingSide): string =>
          (['stereo', 'left', 'right'] as const)
            .map((side) => `<option value="${side}" ${side === selected ? 'selected' : ''}>${side === 'stereo' ? 'Stereo' : side === 'left' ? 'Left' : 'Right'}</option>`)
            .join('');
        routingRow.innerHTML = `
          <div class="setlist-routing-rehearsal">
            <label class="setlist-routing-field">
              <span class="text-dim">Track</span>
              <select class="setlist-routing-select" data-action="set-rehearsal-track-side" data-id="${setlist.id}" aria-label="Track routing">${sideOptions(rehearsal.trackSide)}</select>
            </label>
            <label class="setlist-routing-field">
              <span class="text-dim">Click</span>
              <select class="setlist-routing-select" data-action="set-rehearsal-click-side" data-id="${setlist.id}" aria-label="Click routing">${sideOptions(rehearsal.clickSide)}</select>
            </label>
          </div>
          <span class="text-dim">Choose exactly how the track and click each play back — songs with no click are unaffected.</span>
        `;
      } else {
        routingRow.innerHTML = `
          <select class="setlist-routing-select" data-action="set-routing" data-id="${setlist.id}" aria-label="Track/click routing">
            <option value="tracks-left-click-right" ${setlist.routing === 'tracks-left-click-right' ? 'selected' : ''}>Tracks: Left, Click: Right</option>
            <option value="tracks-right-click-left" ${setlist.routing === 'tracks-right-click-left' ? 'selected' : ''}>Tracks: Right, Click: Left</option>
          </select>
          <span class="text-dim">Splits each song's track from its click — songs with no click just play stereo.</span>
        `;
      }
      body.appendChild(routingRow);

      // Setlist-level count-in override — off by default (each song uses
      // its own configured lead-in, set in the Song Library). When set
      // here, it ALWAYS wins over every song's own value for both the
      // song-start pre-roll and marker-tap seeks in this setlist (see
      // resolveEffectiveLeadIn's own comment) — seconds-only, not the
      // per-song picker's beat-count option, since a single beat count
      // can't apply uniformly across songs with different tempos the way
      // a fixed seconds value can.
      const countInRow = document.createElement('div');
      countInRow.className = 'setlist-mode-row';
      const countInSelectedSeconds = typeof setlist.countInOverride === 'object' && setlist.countInOverride ? setlist.countInOverride.seconds : null;
      countInRow.innerHTML = `
        <label class="setlist-routing-field">
          <span class="text-dim">Count-in</span>
          <select class="setlist-routing-select" data-action="set-setlist-count-in" data-id="${setlist.id}" aria-label="Count-in override">
            <option value="default" ${setlist.countInOverride === null ? 'selected' : ''}>Use each song's own setting</option>
            <option value="off" ${setlist.countInOverride === 'off' ? 'selected' : ''}>Off</option>
            ${MARKER_LEAD_IN_OPTIONS.map((seconds) => `<option value="${seconds}" ${seconds === countInSelectedSeconds ? 'selected' : ''}>${seconds}s</option>`).join('')}
          </select>
        </label>
        <span class="text-dim">Overrides every song's own count-in while playing from this setlist.</span>
      `;
      body.appendChild(countInRow);

      const list = document.createElement('div');
      list.className = 'card-list';
      list.classList.add('setlist-song-list');

      if (setlist.entries.length === 0) {
        list.innerHTML = '<p class="setlist-empty-message">No songs yet. Add the first song below.</p>';
      }

      setlist.entries.forEach((entry, index) => {
        const song = songs.find((s) => s.id === entry.songId);
        const entryEl = document.createElement('div');
        entryEl.className = 'setlist-entry';
        entryEl.draggable = true;
        entryEl.dataset.index = String(index);
        entryEl.dataset.setlistId = setlist.id;

        entryEl.innerHTML = `
          <span class="drag-handle" title="Drag to reorder">&#x2630;</span>
          <span class="setlist-entry-number">${index + 1}.</span>
          <div class="setlist-entry-copy">
            <strong>${song ? escapeHtml(song.name) : '(missing song)'}</strong>
            ${song ? `<span class="text-dim">${modeLabel(song.mode)}</span>` : '<span class="text-danger">This song no longer exists in the library.</span>'}
          </div>
          <div class="reorder-controls">
            <button class="setlist-icon-btn" title="Move up" aria-label="Move ${song ? escapeHtml(song.name) : 'song'} up" data-action="move-up" data-setlist-id="${setlist.id}" data-index="${index}" ${index === 0 ? 'disabled' : ''}>&uarr;</button>
            <button class="setlist-icon-btn" title="Move down" aria-label="Move ${song ? escapeHtml(song.name) : 'song'} down" data-action="move-down" data-setlist-id="${setlist.id}" data-index="${index}" ${index === setlist.entries.length - 1 ? 'disabled' : ''}>&darr;</button>
          </div>
          <button class="setlist-icon-btn setlist-remove-entry" title="Remove from setlist" aria-label="Remove ${song ? escapeHtml(song.name) : 'song'} from setlist" data-action="remove-entry" data-setlist-id="${setlist.id}" data-index="${index}">×</button>
        `;
        list.appendChild(entryEl);
      });

      body.appendChild(list);

      const addRow = document.createElement('div');
      addRow.className = 'setlist-add-row';
      const select = document.createElement('select');
      select.className = 'setlist-add-song-select';
      select.innerHTML = `<option value="">Add song…</option>` + songs.map((s) => `<option value="${s.id}">${escapeHtml(s.name)}</option>`).join('');
      const addBtn = document.createElement('button');
      addBtn.className = 'btn btn-small btn-primary';
      addBtn.textContent = 'Add to Setlist';
      addBtn.addEventListener('click', async () => {
        if (!select.value) return;
        await appStore.addSongToSetlist(setlist.id, select.value);
        select.value = '';
      });
      addRow.appendChild(select);
      addRow.appendChild(addBtn);
      body.appendChild(addRow);
      card.appendChild(body);

      setlistContainer.appendChild(card);
    }

    if (setlistRouteTarget) {
      requestAnimationFrame(() => setlistContainer.querySelector<HTMLElement>('.setlist-editor-card.is-route-target')?.scrollIntoView({ block: 'start' }));
    }
  }

  setlistContainer.addEventListener('click', async (e) => {
    const target = (e.target as HTMLElement).closest<HTMLElement>('[data-action]');
    const action = target?.dataset.action;
    if (!action) return;

    if (action === 'toggle-setlist') {
      const id = target.dataset.id!;
      expandedSetlistId = expandedSetlistId === id ? null : id;
      openActionsSetlistId = null;
      render();
    } else if (action === 'toggle-setlist-actions') {
      const id = target.dataset.id!;
      openActionsSetlistId = openActionsSetlistId === id ? null : id;
      render();
    } else if (action === 'use') {
      const id = target.dataset.id!;
      await appStore.setActiveSetlist(id);
      showToast('Setlist set active in the Player.', 'info');
    } else if (action === 'rename') {
      openActionsSetlistId = null;
      const id = target.dataset.id!;
      const setlist = appStore.getState().setlists.find((s) => s.id === id);
      const newName = await promptDialog({ title: 'Rename Setlist', initialValue: setlist?.name ?? '', confirmLabel: 'Rename' });
      if (newName) await appStore.renameSetlist(id, newName);
    } else if (action === 'set-mode') {
      const id = target.dataset.id!;
      const mode = target.dataset.mode as 'rehearsal' | 'live';
      await appStore.setSetlistMode(id, mode);
    } else if (action === 'duplicate') {
      const id = target.dataset.id!;
      openActionsSetlistId = null;
      const copy = await appStore.duplicateSetlist(id);
      if (copy) {
        expandedSetlistId = copy.id;
        render();
      }
      showToast('Setlist duplicated.', 'info');
    } else if (action === 'export-setlist') {
      const id = target.dataset.id!;
      openActionsSetlistId = null;
      render();
      const setlist = appStore.getState().setlists.find((s) => s.id === id);
      if (!setlist) return;
      try {
        const zip = await appStore.exportFullSetlistZip(APP_VERSION, id);
        const url = URL.createObjectURL(zip);
        const a = document.createElement('a');
        a.href = url;
        const safeName = setlist.name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'setlist';
        a.download = `${safeName}-${new Date().toISOString().slice(0, 10)}.zip`;
        a.click();
        URL.revokeObjectURL(url);
        showToast('Setlist downloaded — send this file to your bandmates.', 'info');
      } catch (error) {
        showToast(error instanceof Error ? error.message : 'Failed to build the setlist file.', 'danger');
      }
    } else if (action === 'delete') {
      const id = target.dataset.id!;
      openActionsSetlistId = null;
      const setlist = appStore.getState().setlists.find((s) => s.id === id);
      const confirmed = await confirmDialog({
        title: 'Delete Setlist',
        message: `Delete setlist "${setlist?.name}"? Songs in your library will not be deleted.`,
        confirmLabel: 'Delete',
        danger: true,
      });
      if (confirmed) {
        if (expandedSetlistId === id) expandedSetlistId = null;
        await appStore.deleteSetlist(id);
        showToast('Setlist deleted.', 'info');
      }
    } else if (action === 'move-up') {
      const setlistId = target.dataset.setlistId!;
      const index = Number(target.dataset.index);
      await appStore.moveSetlistEntry(setlistId, index, index - 1);
    } else if (action === 'move-down') {
      const setlistId = target.dataset.setlistId!;
      const index = Number(target.dataset.index);
      await appStore.moveSetlistEntry(setlistId, index, index + 1);
    } else if (action === 'remove-entry') {
      const setlistId = target.dataset.setlistId!;
      const index = Number(target.dataset.index);
      await appStore.removeSongFromSetlist(setlistId, index);
    }
  });

  // A <select>'s value only changes on 'change' (fired once a new option is
  // actually chosen), not 'click' (which fires on the select itself just to
  // open the dropdown, before any new value exists) — see the routing
  // control's own comment for why this is a select rather than a button row.
  setlistContainer.addEventListener('change', async (e) => {
    const target = e.target as HTMLElement;
    const routingTarget = target.closest<HTMLSelectElement>('[data-action="set-routing"]');
    if (routingTarget) {
      await appStore.setSetlistRouting(routingTarget.dataset.id!, routingTarget.value as SetlistRouting);
      return;
    }
    const countInTarget = target.closest<HTMLSelectElement>('[data-action="set-setlist-count-in"]');
    if (countInTarget) {
      const raw = countInTarget.value;
      const countInOverride: SetlistCountInOverride = raw === 'default' ? null : raw === 'off' ? 'off' : { seconds: Number(raw), beats: null };
      await appStore.setSetlistCountInOverride(countInTarget.dataset.id!, countInOverride);
      return;
    }
    // The two Rehearsal selects (Track/Click) each carry only their OWN
    // side — compose the full RehearsalRouting value from the OTHER
    // select's current value (both selects always exist together, see the
    // rehearsal branch of routingRow above) rather than trying to track
    // the current routing separately from what's actually on screen.
    const trackTarget = target.closest<HTMLSelectElement>('[data-action="set-rehearsal-track-side"]');
    const clickTarget = target.closest<HTMLSelectElement>('[data-action="set-rehearsal-click-side"]');
    const sideTarget = trackTarget ?? clickTarget;
    if (!sideTarget) return;
    const id = sideTarget.dataset.id!;
    const fieldWrap = sideTarget.closest('.setlist-routing-rehearsal')!;
    const trackSide = (trackTarget ?? fieldWrap.querySelector<HTMLSelectElement>('[data-action="set-rehearsal-track-side"]')!).value as RoutingSide;
    const clickSide = (clickTarget ?? fieldWrap.querySelector<HTMLSelectElement>('[data-action="set-rehearsal-click-side"]')!).value as RoutingSide;
    await appStore.setSetlistRouting(id, buildRehearsalRouting(trackSide, clickSide));
  });

  setlistContainer.addEventListener('dragstart', (e) => {
    const target = (e.target as HTMLElement).closest<HTMLElement>('.setlist-entry');
    if (!target) return;
    dragFromIndex = Number(target.dataset.index);
    dragSetlistId = target.dataset.setlistId ?? null;
    target.classList.add('dragging');
  });

  setlistContainer.addEventListener('dragend', (e) => {
    const target = (e.target as HTMLElement).closest<HTMLElement>('.setlist-entry');
    target?.classList.remove('dragging');
    dragFromIndex = null;
    dragSetlistId = null;
  });

  setlistContainer.addEventListener('dragover', (e) => {
    if (dragFromIndex === null) return;
    e.preventDefault();
  });

  setlistContainer.addEventListener('drop', async (e) => {
    const target = (e.target as HTMLElement).closest<HTMLElement>('.setlist-entry');
    if (!target || dragFromIndex === null || dragSetlistId === null) return;
    e.preventDefault();
    const toIndex = Number(target.dataset.index);
    const toSetlistId = target.dataset.setlistId;
    if (toSetlistId !== dragSetlistId) return; // no cross-setlist drag
    await appStore.moveSetlistEntry(dragSetlistId, dragFromIndex, toIndex);
    dragFromIndex = null;
    dragSetlistId = null;
  });

  newSetlistBtn.addEventListener('click', async () => {
    const name = await promptDialog({
      title: 'New Setlist',
      initialValue: `Setlist ${appStore.getState().setlists.length + 1}`,
      confirmLabel: 'Create',
    });
    if (!name) return;
    const setlist = await appStore.createSetlist(name);
    expandedSetlistId = setlist.id;
    render();
  });

  toolsBtn.addEventListener('click', () => {
    const isOpen = toolsBtn.getAttribute('aria-expanded') === 'true';
    toolsBtn.setAttribute('aria-expanded', String(!isOpen));
    toolsPanel.hidden = isOpen;
  });

  exportBtn.addEventListener('click', () => {
    const backup = appStore.exportBackup(APP_VERSION);
    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pale-moonlight-backup-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    showToast('Backup exported. Remember: WAV audio files are not included.', 'info');
  });

  importBtn.addEventListener('click', () => backupInput.click());
  backupInput.addEventListener('change', async () => {
    const file = backupInput.files?.[0];
    backupInput.value = '';
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text) as BackupFileV1;
      if (parsed.formatVersion !== 1 || !Array.isArray(parsed.songs) || !Array.isArray(parsed.setlists)) {
        throw new Error('This file is not a recognized backup format.');
      }
      const confirmed = await confirmDialog({
        title: 'Import Backup',
        message: `This will add ${parsed.songs.length} song entries and ${parsed.setlists.length} setlist(s). Audio files are NOT included and must be re-imported. Continue?`,
        confirmLabel: 'Import',
      });
      if (!confirmed) return;
      await appStore.importBackup(parsed);
      showToast('Backup imported. Re-import WAV files for each song in the Library.', 'warn');
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to import backup file.', 'danger');
    }
  });

  exportFullSetlistBtn.addEventListener('click', async () => {
    exportFullSetlistBtn.disabled = true;
    try {
      const zip = await appStore.exportFullSetlistZip(APP_VERSION);
      const url = URL.createObjectURL(zip);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pale-moonlight-setlist-${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      showToast('Setlist downloaded — send this file to your bandmates.', 'info');
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to build the setlist file.', 'danger');
    } finally {
      exportFullSetlistBtn.disabled = false;
    }
  });

  async function confirmAndImportFullSetlist(backup: BackupFileV1, audioBySongId: Map<string, Blob>): Promise<void> {
    const existingSetlists = appStore.getState().setlists;
    const versionNotes = backup.setlists
      .map((incoming) => {
        const local = existingSetlists.find((s) => s.id === incoming.id);
        if (!local || local.updatedAt === incoming.updatedAt) return null;
        return `You already have "${incoming.name}" (yours: ${new Date(local.updatedAt).toLocaleString()}, this package: ${new Date(incoming.updatedAt).toLocaleString()}) — importing will overwrite yours.`;
      })
      .filter((line): line is string => Boolean(line));
    const confirmed = await confirmDialog({
      title: 'Open Shared Setlist',
      message: [
        `This will add ${backup.songs.length} song(s) and ${backup.setlists.length} setlist(s), with audio included for ${audioBySongId.size} of them.`,
        ...versionNotes,
        'Continue?',
      ].join(' '),
      confirmLabel: 'Add Everything',
    });
    if (!confirmed) return;
    await appStore.importFullSetlist(backup, audioBySongId);
    showToast('Setlist imported — everything is ready to play.', 'info');
  }

  importFullSetlistBtn.addEventListener('click', () => fullSetlistInput.click());
  fullSetlistInput.addEventListener('change', async () => {
    const file = fullSetlistInput.files?.[0];
    fullSetlistInput.value = '';
    if (!file) return;
    importFullSetlistBtn.disabled = true;
    try {
      const { backup, audioBySongId } = await parseFullSetlistZip(file);
      await confirmAndImportFullSetlist(backup, audioBySongId);
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to open this setlist file.', 'danger');
    } finally {
      importFullSetlistBtn.disabled = false;
    }
  });

  const unsubscribe = appStore.subscribe(render);
  render();
  return unsubscribe;
}

function modeLabel(mode: string): string {
  if (mode === 'tracks-click') return 'Tracks + Click';
  if (mode === 'metronome') return 'Metronome Only';
  return 'Tracks Only';
}

function escapeHtml(value: string): string {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}
